import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Send,
  Search,
  FileText,
  CheckCircle,
  Clock,
  AlertCircle,
  ShieldCheck,
  ArrowRight,
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { usePageTracking } from "../hooks/usePageTracking";
import { safeJsonLd } from "../lib/safeJsonLd";

const STAGES = [
  {
    id: "submitted",
    label: "Submitted",
    icon: Send,
    color: "border-blue-500/40 bg-blue-500/5",
    accent: "text-blue-400",
    sample: "Case #SDC-2026-0142 received Feb 14, 2026 · 11:42 PT",
    whatHappens:
      "Your intake form lands in our case queue with a unique Case ID. You receive an email confirmation within minutes containing the ID and a link to track status here on the site.",
    whatYouSee: "Confirmation email · Case ID assigned · ETA for first review",
    duration: "Same day",
  },
  {
    id: "under_review",
    label: "Under Review",
    icon: Search,
    color: "border-yellow-500/40 bg-yellow-500/5",
    accent: "text-yellow-400",
    sample: "Reviewing platform policies cited (Community Standards §3.1)",
    whatHappens:
      "We review the platform's specific cited policy, your account history, and the suspension/disable email word-for-word. We map each cited violation to the platform's published policy language.",
    whatYouSee: "Status update · Reviewer notes (if you ask) · Policy citations",
    duration: "1-2 business days",
  },
  {
    id: "documentation_in_progress",
    label: "Documentation In Progress",
    icon: FileText,
    color: "border-orange-500/40 bg-orange-500/5",
    accent: "text-orange-400",
    sample: "Drafting 9-page appeal package · Evidence index assembled",
    whatHappens:
      "We draft your full appeal package: cited-policy rebuttal, evidence index, timeline of account activity, account ownership proof, and a written request for human review. Length is typically 4–12 pages depending on case complexity.",
    whatYouSee: "Progress updates · Optional draft preview for client review",
    duration: "2-5 business days (24h for Priority)",
  },
  {
    id: "documentation_complete",
    label: "Documentation Complete",
    icon: CheckCircle,
    color: "border-lime-500/40 bg-lime-500/5",
    accent: "text-lime-400",
    sample: "Final package: 7 pages + 11 evidence exhibits · Ready for submission",
    whatHappens:
      "Final package is locked. You receive a copy via email. With your authorization we proceed to submission, or you can submit it yourself through the official appeal channels.",
    whatYouSee: "Full PDF copy of your package · Submission consent form",
    duration: "Same day as previous stage",
  },
  {
    id: "sent_to_platform",
    label: "Sent to Platform",
    icon: Send,
    color: "border-purple-500/40 bg-purple-500/5",
    accent: "text-purple-400",
    sample: "Submitted via Meta Business Help Center · Reference #4811-AC2",
    whatHappens:
      "We submit the appeal through the platform's official public appeal channel — the same form anyone can use, but with our prepared package. We log the submission reference number and any auto-acknowledgment.",
    whatYouSee: "Submission timestamp · Platform reference number · Screenshot of submission",
    duration: "Within 1 business day of completion",
  },
  {
    id: "awaiting_response",
    label: "Awaiting Response",
    icon: Clock,
    color: "border-indigo-500/40 bg-indigo-500/5",
    accent: "text-indigo-400",
    sample: "Day 8 of platform review window · Auto follow-up scheduled day 14",
    whatHappens:
      "The platform reviews the appeal. This is entirely outside our control. We send polite follow-ups at the 14-day and 28-day mark if no response. Most platforms acknowledge within 10 days; final decisions range from 14 to 60 days.",
    whatYouSee: "Day counter · Any platform correspondence we receive · Follow-up log",
    duration: "14 - 60 days",
  },
  {
    id: "response_received",
    label: "Response Received",
    icon: AlertCircle,
    color: "border-cyan-500/40 bg-cyan-500/5",
    accent: "text-cyan-400",
    sample: "Platform reviewer requested additional ID verification",
    whatHappens:
      "Whether the platform responds with an approval, denial, or request for more information, we forward it to you the same business day. If they request more information, we prepare a second-round response at no extra cost (for Appeal Package and Priority Support clients).",
    whatYouSee: "Full platform response · Our recommendation · Next-step options",
    duration: "Same day as platform response",
  },
  {
    id: "resolved",
    label: "Resolved",
    icon: CheckCircle,
    color: "border-green-500/40 bg-green-500/5",
    accent: "text-green-400",
    sample: "Platform completed review · Outcome forwarded to client",
    whatHappens:
      "The platform issued a final decision. We do not consider a case 'won' or 'lost' — we consider it resolved when the platform has rendered a decision and you have been informed.",
    whatYouSee: "Final platform decision · Closing case summary · Optional review request",
    duration: "Same day as final response",
  },
  {
    id: "closed",
    label: "Closed",
    icon: CheckCircle,
    color: "border-slate-500/40 bg-slate-500/5",
    accent: "text-slate-300",
    sample: "Case archived · Documentation retained for 12 months",
    whatHappens:
      "Case is archived. We retain your documentation for 12 months in case you need it for future reference (e.g., a second suspension on the same account, or for a different appeal venue). After that, records are permanently deleted per our Privacy Policy.",
    whatYouSee: "Archive confirmation · Retention timeline · Data deletion option",
    duration: "Permanent (with retention policy)",
  },
];

export default function CaseWorkflowPage() {
  usePageTracking("Case Workflow Process", { category: "trust" });

  // SEO: set title, meta description, canonical, and Article schema
  useEffect(() => {
    document.title = "How Our Case Workflow Works | Social Dispute Co";

    let metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute(
        "content",
        "See exactly how every social media appeal case moves through 9 status stages — from intake to closure. Full operational transparency from Social Dispute Co."
      );
    }

    let canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute("href", "https://socialdisputeco.com/process");
    }

    return () => {
      document.title = "Social Media Account Appeal Documentation | Social Dispute Co.";
    };
  }, []);

  const schema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Social Media Account Appeal Documentation Workflow",
    "provider": {
      "@type": "Organization",
      "name": "Social Dispute Co.",
      "url": "https://socialdisputeco.com",
    },
    "description":
      "9-stage transparent case workflow for social media account appeal documentation preparation and submission.",
    "areaServed": "United States",
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Case Stages",
      "itemListElement": STAGES.map((s, i) => ({
        "@type": "Offer",
        "position": i + 1,
        "name": s.label,
        "description": s.whatHappens,
      })),
    },
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* eslint-disable-next-line react/no-danger */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: safeJsonLd(schema) }} />

      <Header />

      <main className="py-16 md:py-24">
        <div className="mx-auto max-w-5xl px-6">
          {/* Hero */}
          <div className="text-center mb-16" data-testid="process-hero">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/15 border border-blue-500/30 text-blue-300 text-xs font-medium mb-6">
              <ShieldCheck className="w-3.5 h-3.5" />
              Full Operational Transparency
            </div>
            <h1 className="text-4xl md:text-5xl font-bold leading-tight">
              How Your Case Moves Through
              <span className="block text-blue-400">9 Status Stages</span>
            </h1>
            <p className="mt-6 text-lg text-slate-300 max-w-2xl mx-auto leading-relaxed">
              Every case in our system is tracked through the same nine stages. You can see where your
              case is at any time via your unique Case ID. Below is the exact workflow, with sample
              data from a real (anonymized) case.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/track-case"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-white text-slate-950 px-6 py-3 text-sm font-semibold hover:bg-slate-100 transition"
                data-testid="process-track-cta"
              >
                Track an Existing Case
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-white/15 px-6 py-3 text-sm font-medium hover:bg-white/5 transition"
                data-testid="process-start-cta"
              >
                Start a New Case
              </Link>
            </div>
          </div>

          {/* Timeline of stages */}
          <ol className="space-y-6" data-testid="process-stages">
            {STAGES.map((stage, i) => {
              const Icon = stage.icon;
              return (
                <li
                  key={stage.id}
                  className={`relative rounded-2xl border ${stage.color} p-6 md:p-8`}
                  data-testid={`process-stage-${stage.id}`}
                >
                  <div className="flex items-start gap-5">
                    {/* Stage number + icon */}
                    <div className="flex-shrink-0 flex flex-col items-center">
                      <div className="w-12 h-12 rounded-xl bg-slate-900 border border-white/15 flex items-center justify-center">
                        <Icon className={`w-5 h-5 ${stage.accent}`} />
                      </div>
                      <span className="mt-2 text-xs font-mono text-slate-500">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                    </div>

                    {/* Stage content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-3 mb-3">
                        <h2 className={`text-xl md:text-2xl font-bold ${stage.accent}`}>
                          {stage.label}
                        </h2>
                        <span className="text-xs px-2.5 py-1 rounded-full bg-white/10 text-slate-300">
                          {stage.duration}
                        </span>
                      </div>

                      {/* Sample mock data block (anonymized) */}
                      <div className="mb-4 rounded-lg bg-slate-900/70 border border-white/10 px-4 py-2.5">
                        <p className="text-xs text-slate-500 mb-0.5">Sample case data:</p>
                        <p className="text-sm font-mono text-slate-300">{stage.sample}</p>
                      </div>

                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1.5">
                            What happens
                          </p>
                          <p className="text-sm text-slate-300 leading-relaxed">{stage.whatHappens}</p>
                        </div>
                        <div>
                          <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1.5">
                            What you see
                          </p>
                          <p className="text-sm text-slate-300 leading-relaxed">{stage.whatYouSee}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </li>
              );
            })}
          </ol>

          {/* Footer disclaimer + CTA */}
          <div className="mt-16 rounded-2xl border border-white/10 bg-white/5 p-8 text-center">
            <h3 className="text-2xl font-bold mb-3">No Black Box. No Surprises.</h3>
            <p className="text-slate-300 max-w-2xl mx-auto leading-relaxed">
              Most "recovery" services tell you nothing about how they work. We document everything.
              Every status change is logged, every submission is timestamped, and every platform
              response is forwarded to you the same business day.
            </p>
            <p className="mt-4 text-xs text-slate-500 max-w-2xl mx-auto">
              Platform decisions are entirely outside our control. We never guarantee outcomes —
              only that the documentation work itself will be thorough, on time, and submitted
              through official appeal channels.
            </p>
            <div className="mt-6">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-blue-500 text-white px-6 py-3 text-sm font-semibold hover:bg-blue-600 transition"
                data-testid="process-final-cta"
              >
                Check If Your Case Qualifies
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
